/**
 * PartnerActivityTimeline — Customer 360° vertical timeline (E-104)
 *
 * Megjelenít: Task (NOTE/CALL/TODO) + Invoice + Quote + DeliveryNote +
 * Rental + Worksheet + LoyaltyHistory eseményeket egyetlen kronológikus listában.
 * Inline quick-add NOTE akciót biztosít.
 */

import type { ActivityItem, ActivityType } from '@/api/partners';
import { ACTIVITY_TYPES } from '@/api/partners';
import { Button } from '@/components/ui';
import { usePartnerActivity } from '@/hooks/use-partner-activity';
import { useTranslation } from '@kgc/i18n';
import {
  Banknote,
  FileText,
  Loader2,
  MessageSquare,
  Package,
  Phone,
  Receipt,
  Star,
  StickyNote,
  Truck,
  Wrench,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { QuickAddNoteDialog } from './QuickAddNoteDialog';

interface Props {
  partnerId: string;
  /** Optional — if omitted, backend resolves the default location */
  locationId?: string;
  /** Optional limit per page (default 30) */
  limit?: number;
}

export function PartnerActivityTimeline({ partnerId, locationId, limit = 30 }: Props) {
  const { t } = useTranslation();
  const [activeFilters, setActiveFilters] = useState<ActivityType[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const queryOpts = useMemo(
    () => ({
      types: activeFilters.length > 0 ? activeFilters : undefined,
      limit,
    }),
    [activeFilters, limit]
  );

  const { items, isLoading, isLoadingMore, error, hasMore, loadMore, prependNote, refetch } =
    usePartnerActivity(partnerId, queryOpts);

  const toggleFilter = (type: ActivityType) => {
    setActiveFilters(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const toggleExpand = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <section aria-label={t('partner.timeline.title')} className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t('partner.timeline.title')}</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setQuickAddOpen(true)}>
            <StickyNote className="mr-1 h-4 w-4" />
            {t('partner.timeline.quickAddNote')}
          </Button>
        </div>
      </div>

      <FilterChips active={activeFilters} onToggle={toggleFilter} />

      {error && (
        <div
          role="alert"
          className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {t('partner.timeline.loadError')}: {error}
          <Button variant="link" size="sm" onClick={() => void refetch()} className="ml-2">
            {t('common.retry')}
          </Button>
        </div>
      )}

      {isLoading && items.length === 0 ? (
        <TimelineSkeleton />
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <ol className="relative space-y-3 border-l-2 border-border pl-6">
          {items.map((item, idx) => {
            const key = `${item.type}-${item.id}`;
            return (
              <TimelineRow
                key={key}
                item={item}
                isFirst={idx === 0}
                isExpanded={expanded.has(key)}
                onToggleExpand={() => toggleExpand(key)}
              />
            );
          })}
        </ol>
      )}

      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void loadMore()}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              t('partner.timeline.loadMore')
            )}
          </Button>
        </div>
      )}

      <QuickAddNoteDialog
        open={quickAddOpen}
        onOpenChange={setQuickAddOpen}
        partnerId={partnerId}
        locationId={locationId}
        onCreated={note => {
          prependNote(note);
          setQuickAddOpen(false);
        }}
      />
    </section>
  );
}

// ============================================
// Subcomponents
// ============================================

function FilterChips({
  active,
  onToggle,
}: {
  active: ActivityType[];
  onToggle: (t: ActivityType) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-wrap gap-1.5"
      role="group"
      aria-label={t('partner.timeline.filterLabel')}
    >
      {ACTIVITY_TYPES.map(type => {
        const isActive = active.includes(type);
        return (
          <button
            key={type}
            type="button"
            onClick={() => onToggle(type)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              isActive
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-muted-foreground hover:bg-muted'
            }`}
            aria-pressed={isActive}
          >
            {t(`partner.timeline.types.${type}`)}
          </button>
        );
      })}
    </div>
  );
}

function TimelineRow({
  item,
  isFirst,
  isExpanded,
  onToggleExpand,
}: {
  item: ActivityItem;
  isFirst: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
}) {
  const { t } = useTranslation();
  const Icon = getIconForType(item);
  const hasDetails = Boolean(item.summary || item.metadata);

  return (
    <li className="relative">
      <span
        className={`absolute -left-[33px] flex h-6 w-6 items-center justify-center rounded-full border-2 ${
          isFirst
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-background text-muted-foreground'
        }`}
        aria-hidden="true"
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="rounded-md border border-border bg-background p-3 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-semibold text-foreground">{item.title}</span>
              {item.subtype && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                  {item.subtype}
                </span>
              )}
              {item.status && <StatusPill status={item.status} />}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              <time dateTime={item.occurredAt}>{formatDateTime(item.occurredAt)}</time>
              <span className="mx-1">·</span>
              <span>{t(`partner.timeline.types.${item.type}`)}</span>
            </div>
          </div>
          {item.amount !== undefined && (
            <span className="text-sm font-medium tabular-nums text-foreground">
              {formatHuf(item.amount)}
            </span>
          )}
        </div>

        {hasDetails && (
          <>
            <button
              type="button"
              onClick={onToggleExpand}
              className="mt-2 text-xs text-primary underline-offset-2 hover:underline"
              aria-expanded={isExpanded}
            >
              {isExpanded ? t('partner.timeline.collapse') : t('partner.timeline.expand')}
            </button>
            {isExpanded && (
              <div className="mt-2 space-y-1 border-t border-border pt-2 text-xs text-muted-foreground">
                {item.summary && <p className="whitespace-pre-wrap">{item.summary}</p>}
                {item.metadata && (
                  <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                    {Object.entries(item.metadata).map(([k, v]) => (
                      <div key={k} className="contents">
                        <dt className="font-medium">{translateMetaKey(t, k)}:</dt>
                        <dd>{formatMetaValue(k, v)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </li>
  );
}

function StatusPill({ status }: { status: string }) {
  return (
    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      {status}
    </span>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="rounded border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
      {t('partner.timeline.empty')}
    </div>
  );
}

function TimelineSkeleton() {
  return (
    <div className="space-y-3" aria-hidden="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-16 animate-pulse rounded bg-muted/40" />
      ))}
    </div>
  );
}

// ============================================
// Helpers
// ============================================

function getIconForType(item: ActivityItem) {
  if (item.type === 'TASK') {
    if (item.subtype === 'CALL') return Phone;
    if (item.subtype === 'NOTE') return StickyNote;
    return MessageSquare;
  }
  switch (item.type) {
    case 'INVOICE':
      return Receipt;
    case 'QUOTE':
      return FileText;
    case 'DELIVERY_NOTE':
      return Truck;
    case 'RENTAL':
      return Package;
    case 'WORKSHEET':
      return Wrench;
    case 'LOYALTY':
      return Star;
    default:
      return Banknote;
  }
}

export function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function formatHuf(n: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(n);
}

// Metadata keys whose numeric value represents HUF currency — format as HUF
const CURRENCY_KEYS = new Set([
  'paidAmount',
  'balanceDue',
  'depositRequired',
  'depositPaid',
  'depositReturned',
  'totalSpend',
]);

function formatMetaValue(key: string, v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Igen' : 'Nem';
  if (typeof v === 'number') {
    if (CURRENCY_KEYS.has(key)) return formatHuf(v);
    return new Intl.NumberFormat('hu-HU').format(v);
  }
  if (typeof v === 'string') {
    // ISO date → pretty print; date-only ISO (2026-04-08) → date without time
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return formatDateTime(v);
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      return new Date(v).toLocaleDateString('hu-HU');
    }
    return v;
  }
  return JSON.stringify(v);
}

/**
 * Translate metadata key to human-readable label. Falls back to the raw key
 * if the i18n entry is missing. @kgc/i18n returns the full key string when
 * translation is missing — we detect that and show the camelCase key instead.
 */
function translateMetaKey(t: (key: string) => string, key: string): string {
  const full = `partner.timeline.metaLabels.${key}`;
  const translated = t(full);
  return translated === full ? key : translated;
}
